import React, { useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import {
  AlertTriangle,
  BellRing,
  CheckCircle,
  Info,
} from "lucide-react";
import { TabProps } from "@features/mx/device-management/types";
import { useAlerts } from "@features/mx/device-management/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { PageHeader } from "@shared/components/PageHeader";

const AlertsTab: React.FC<TabProps> = ({tab, meterId}) => {

  const {data: alerts } = useAlerts({meterId})

  useEffect(() => {
    const alertTypes = [...new Set(alerts?.map(a => a.status?.toLowerCase()))];
    logEvent("MX Alerts Viewed", {
      meterId,
      alertTypes,
      count: alerts?.length,
    });
}, [alerts, meterId]);

  const getAlertIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case "rcnt":
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case "faulty":
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case "normal":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      default:
        return <Info className="h-5 w-5" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "faulty":
        return "secondary";
      case "rcnt":
        return "default";
      case "normal":
        return "secondary";
      default:
        return "outline";
    }
  };

  return (
    <Card className="shadow-sm">
      <CardHeader className="py-2 px-3">
        <div className="flex justify-between items-center">
        
<PageHeader
  title="Alerts & Warnings"
  icon={AlertTriangle}
/>
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-0">
        {alerts?.length > 0 ? (
          <div className="space-y-2">
            {alerts?.map((alert) => (
              <div
                key={alert.id}
                className="flex items-start p-2 border rounded-md"
              >
                <div className="mr-2 mt-0.5">{getAlertIcon(alert.status)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <Badge variant={getStatusColor(alert.status)}>
                      {alert.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {alert.updatedOn}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-6 text-center">
            <div className="mx-auto w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mb-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-sm font-medium mb-1">No Alerts</h3>
            <p className="text-xs text-muted-foreground">
              This meter has no active alerts or warnings.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AlertsTab;